import { TFormField, TValidation } from '@nirmata/shared';
import { CheckboxField } from '@nirmata/web-components/basic/checkbox-field';
import { Button } from '@nirmata/web-components/ui/button';
import { Input } from '@nirmata/web-components/ui/input';
import { ArrowDown, ArrowUp, CheckCircle, Maximize2, Minimize2, Regex, Save } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

import { FORM_FIELDS_CONFIG } from '../../fields/fields.config';

const VALIDATION_META = {
  required: { label: 'Required', Icon: CheckCircle },
  min: { label: 'Min', Icon: ArrowDown },
  max: { label: 'Max', Icon: ArrowUp },
  minLength: { label: 'Min Length', Icon: Minimize2 },
  maxLength: { label: 'Max Length', Icon: Maximize2 },
  pattern: { label: 'Pattern', Icon: Regex },
} as const;

interface ValidationsEditorProps {
  field: TFormField;
  onChange: (updates: { validationRules: TValidation[] }) => void;
}

function getSupportedValidations(field: TFormField): string[] {
  for (const group of Object.values(FORM_FIELDS_CONFIG)) {
    for (const config of group) {
      if (config.defaultProps?.componentType === field.componentType) {
        return config.validations || [];
      }
    }
  }
  return [];
}

function getInitialValidations(field: TFormField): TValidation[] {
  return Array.isArray(field.validationRules) ? field.validationRules : [];
}

function findValidation(validations: TValidation[], ruleType: string): TValidation | undefined {
  return validations.find((v) => v.ruleType === ruleType);
}

export function ValidationsEditor({ field, onChange }: ValidationsEditorProps) {
  const supported = getSupportedValidations(field);
  const [validations, setValidations] = useState<TValidation[]>(getInitialValidations(field));

  const updateValidation = (ruleType: string, ruleValue: unknown) => {
    let updated: TValidation[];
    const existing = findValidation(validations, ruleType);
    if (ruleValue === '' || ruleValue === false || ruleValue == null) {
      updated = validations.filter((v) => v.ruleType !== ruleType);
    } else if (existing) {
      updated = validations.map((v) => (v.ruleType === ruleType ? { ...v, ruleValue } : v));
    } else {
      updated = [...validations, { ruleType, ruleValue, errorMessage: '' }];
    }
    setValidations(updated);
  };

  const updateErrorMessage = (ruleType: string, errorMessage: string) => {
    const updated = validations.map((v) => (v.ruleType === ruleType ? { ...v, errorMessage } : v));
    setValidations(updated);
  };

  const handleDelete = (ruleType: string) => {
    const updated = validations.filter((v) => v.ruleType !== ruleType);
    setValidations(updated);
  };

  const handleSave = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    onChange({ validationRules: validations });
    toast.success('Validation rules saved successfully!');
  };

  if (supported.length === 0) {
    return (
      <div className="flex flex-col gap-7 p-4">
        <div className="text-sm text-[var(--muted-foreground)]">
          No validation rules supported for this field.
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-7 p-4">
      {supported.includes('required') && (
        <div className="flex flex-col gap-1">
          <div className="flex items-center gap-2 mb-1">
            <CheckCircle className="h-4 w-4 text-[var(--muted-foreground)]" />
            <span className="font-medium text-[var(--muted-foreground)]">Required</span>
          </div>
          <CheckboxField
            id="required"
            name="required"
            label="Required"
            checked={!!findValidation(validations, 'required')?.ruleValue}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              e.preventDefault();
              updateValidation('required', e.target.checked);
            }}
          />
          {findValidation(validations, 'required') && (
            <Input
              type="text"
              className="mt-1"
              placeholder="Error message"
              value={findValidation(validations, 'required')?.errorMessage || ''}
              onChange={(e) => updateErrorMessage('required', e.target.value)}
            />
          )}
        </div>
      )}
      {supported.map((rule, idx) =>
        rule !== 'required' ? (
          <div key={rule} className="flex flex-col gap-1">
            <div className="flex items-center gap-2 mb-1">
              {(() => {
                const meta = VALIDATION_META[rule as keyof typeof VALIDATION_META];
                const Icon = meta?.Icon;
                return Icon ? <Icon className="h-4 w-4 text-[var(--muted-foreground)]" /> : null;
              })()}
              <span className="font-medium text-[var(--muted-foreground)]">
                {VALIDATION_META[rule as keyof typeof VALIDATION_META]?.label || rule}
              </span>
            </div>
            <label className="flex flex-col gap-1">
              {rule === 'min' && 'Min'}
              {rule === 'max' && 'Max'}
              {rule === 'minLength' && 'Min Length'}
              {rule === 'maxLength' && 'Max Length'}
              {rule === 'pattern' && 'Pattern'}
              <Input
                type={rule === 'pattern' ? 'text' : 'number'}
                value={findValidation(validations, rule)?.ruleValue ?? ''}
                onChange={(e) =>
                  updateValidation(
                    rule,
                    rule === 'pattern'
                      ? e.target.value
                      : e.target.value === ''
                        ? undefined
                        : Number(e.target.value)
                  )
                }
                placeholder={rule === 'pattern' ? 'e.g. ^[A-Za-z]+$' : undefined}
              />
            </label>
            {findValidation(validations, rule) && (
              <div className="flex items-center gap-2 mt-1">
                <Input
                  type="text"
                  className="flex-1"
                  placeholder="Error message"
                  value={findValidation(validations, rule)?.errorMessage || ''}
                  onChange={(e) => updateErrorMessage(rule, e.target.value)}
                />
                <button
                  type="button"
                  className="text-red-500 hover:underline ml-2"
                  onClick={() => handleDelete(rule)}
                  title="Delete validation"
                >
                  Delete
                </button>
              </div>
            )}
          </div>
        ) : null
      )}
      <Button type="button" className="mt-4 self-end cursor-pointer" onClick={handleSave}>
        <Save className="h-4 w-4 mr-2" />
        Save Validation Rules
      </Button>
    </div>
  );
}
